import {Box, merge, Text} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import clsx from 'clsx'
import {useListViewId} from './IdContext'
import {useListViewVariant} from './VariantContext'
import {testIdProps} from '@github-ui/test-id-props'
import {type FunctionComponent, type PropsWithChildren, useId} from 'react'
import {defaultContainerStyles as defaultLeadingContentStyles} from '../ListItem/LeadingContent'
import type {IconProps} from '@primer/octicons-react'

const liSx: BetterSystemStyleObject = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  py: '10px',
  position: 'relative',
  display: 'grid',
  flexDirection: 'row',
  minHeight: '2rem' /* 32px */,
  fontSize: 1,
  color: 'fg.default',
  backgroundColor: 'canvas.subtle',
  alignItems: 'center',
  gridTemplateRows: 'auto',
  gridTemplateAreas: '"status primary secondary"',
  gridTemplateColumns: 'minmax(24px, auto) minmax(0, auto) 1fr',

  '&.listviewnote-compact': {
    minHeight: '1rem',
    py: 2,
  },

  '&:focus-visible': {
    outline: '2px solid var(--focus-outlineColor, var(--color-accent-fg))',
    outlineOffset: '-2px',
    boxShadow: 'none',
    borderRadius: 0,
  },
}

const titleSx: BetterSystemStyleObject = {gridArea: 'primary', pr: 2}

const borderColorSx: BetterSystemStyleObject = {
  borderTopColor: 'border.muted',
  borderBottomColor: 'border.muted',
}

export type BorderSx = Pick<
  React.CSSProperties,
  | 'borderTop'
  | 'borderBottom'
  | 'borderTopLeftRadius'
  | 'borderTopRightRadius'
  | 'borderBottomLeftRadius'
  | 'borderBottomRightRadius'
>

export interface NoteProps {
  /**
   * The text content of the note
   */
  title: string
  /**
   * The icon to render within the item but before its title
   */
  leadingIcon?: FunctionComponent<PropsWithChildren<IconProps>>
  /**
   * The border styles to apply to the note, border color theming will be applied automatically
   */
  borderSx?: BorderSx
}

export const Note = ({title, borderSx, leadingIcon: LeadingIcon, children}: PropsWithChildren<NoteProps>) => {
  const {idPrefix} = useListViewId()
  const {variant} = useListViewVariant()
  const uniqueIdSuffix = useId()
  const titleId = `${idPrefix}-listview-note-${uniqueIdSuffix}`
  const borderSxWithDefaults = borderSx ? merge(borderSx, borderColorSx) : undefined
  const containerSx = borderSxWithDefaults ? merge<BetterSystemStyleObject>(liSx, borderSxWithDefaults) : liSx
  const leadingContentSx = merge<BetterSystemStyleObject>(defaultLeadingContentStyles, {ml: 2})

  return (
    <Box
      sx={containerSx}
      as="li"
      role="listitem"
      className={clsx(variant === 'compact' && 'listviewnote-compact', 'listviewnote')}
      aria-labelledby={titleId}
      tabIndex={-1} // Handled by useFocusZone with roving tabIndex
      {...testIdProps('listviewnote')}
    >
      <Box sx={leadingContentSx}>{LeadingIcon && <LeadingIcon />}</Box>
      <Text id={titleId} sx={titleSx} {...testIdProps('listviewnote-title')}>
        {title}
      </Text>
      <Box sx={{gridArea: 'secondary'}}>{children}</Box>
    </Box>
  )
}

try{ Note.displayName ||= 'Note' } catch {}